/** @format */

import { useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiMail,
  FiMapPin,
  FiMessageSquare,
  FiPackage,
  FiPhone,
  FiTrash2,
  FiTruck,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../api/api";
import {
  delete_order_product,
  get_order,
} from "../../store/reducers/orderReducer";
import QuantityInput from "../../utils/QuantityInput";
import {
  BuyerSection,
  devise,
  TruncateText,
  WhatsAppButton,
} from "../../utils/utils";

const StatusBadge = ({ value, type }) => {
  const isPaid = value === "paid";
  const isDelivered = value === "delivered";
  const isPositive = type === "payment" ? isPaid : isDelivered;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize
      ${isPositive ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"}`}
    >
      {isPositive ? <FiCheckCircle size={11} /> : <FiClock size={11} />}
      {value}
    </span>
  );
};

const AddressDisplay = ({ address }) => {
  const { t } = useTranslation();
  if (!address)
    return <p className='text-gray-400 text-sm'>{t("Unknown Address")}</p>;

  const g = (a, b) => a || b;
  const name = g(address.name, address.info?.name);
  const phone = g(address.phone, address.info?.phone);
  const addr = g(address.address, address.info?.address);
  const apt = g(address.apartment, address.info?.apartment);
  const city = g(address.city, address.info?.city);
  const region = g(address.region, address.info?.region);
  const country = g(address.country, address.info?.country);
  const postal = g(address.postalCode, address.info?.postalCode);
  const isDefault = g(address.default, address.info?.default);

  return (
    <div className='flex flex-col gap-1 text-sm text-gray-700'>
      <p className='font-semibold text-gray-900'>
        {name} <span className='font-normal text-gray-500'>· {phone}</span>
      </p>
      <p>
        {addr}
        {apt ? `, ${apt}` : ""}
      </p>
      <p>
        {city}, {region}, {country} {postal}
      </p>
      {isDefault && (
        <span className='inline-flex items-center gap-1 text-xs text-emerald-600 font-medium'>
          <FiCheckCircle size={11} /> {t("default address")}
        </span>
      )}
    </div>
  );
};

const OrderDetails = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const { MyOrder } = useSelector((state) => state.order);
  const { customerInfo } = useSelector((state) => state.auth);
  const { t } = useTranslation();

  useEffect(() => {
    if (!orderId) return;
    dispatch(get_order({ orderId }))
      .unwrap()
      .catch((err) => console.error("get_order failed:", err));
  }, [orderId, dispatch]);

  const handleDeleteOrder = (product) => {
    dispatch(
      delete_order_product({
        orderId,
        sellerId: product.sellerId,
        cardId: product.cardId,
      }),
    ).then(() => dispatch(get_order({ orderId })));
  };

  const getDiscountedPrice = (product) => {
    const discount = product.discount || 0;
    const base = product.selectedVariant
      ? product.selectedVariant.variantPrice
      : product.price;
    return Math.floor(base - (base * discount) / 100);
  };

  const handleSendOrderMessage = async (seller) => {
    if (orderId !== MyOrder.orderId || !customerInfo?.id)
      return toast.error("Le message n'a pas abouti, vérifier la commande");
    try {
      const payload = {
        senderName: customerInfo.name,
        senderId: customerInfo.id,
        receiverId: seller.sellerId,
        shopName: seller.shopInfo.shopName,
        orderId,
        totalPrice: seller.sellerTotal,
        products: seller.products.map((p) => ({
          image: p.selectedVariant
            ? p.selectedVariant.variantImage
            : p.images[0]?.url || p.images[0],
          title: p.selectedVariant
            ? `${p.name} ${p.selectedVariant.color || ""} ${p.selectedVariant.size || ""}`.trim()
            : p.name,
          quantity: p.quantity,
          unitPrice: getDiscountedPrice(p),
          productId: p._id,
        })),
      };
      dispatch({ type: "chat/updateFdMessages", payload });
      await api.post(
        `/api/chat/customer/message/send-order/${customerInfo.id}`,
        payload,
      );
      navigate(`/dashboard/my_chats/${seller.sellerId}`);
    } catch {
      toast.error(t("Erreur lors de l'envoi du message"));
    }
  };

  const buildOrderMessage = (seller) => ({
    messageType: "order",
    orderInfo: {
      shopName: seller.shopInfo.shopName,
      orderId,
      totalPrice: seller.sellerTotal,
      products: seller.products.map((p) => ({
        image: p.selectedVariant
          ? p.selectedVariant.variantImage
          : p.images[0]?.url || p.images[0],
        title: p.selectedVariant
          ? `${p.name} ${p.selectedVariant.color || ""} ${p.selectedVariant.size || ""}`.trim()
          : p.name,
        quantity: p.quantity,
        unitPrice: getDiscountedPrice(p),
        productId: p._id,
      })),
    },
  });

  if (!MyOrder || !MyOrder.sellers || MyOrder.sellers.length === 0) {
    return (
      <div className='min-h-[300px] flex flex-col items-center justify-center gap-4 bg-white rounded-2xl p-10 text-center'>
        <div className='w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center'>
          <FiPackage size={28} className='text-gray-400' />
        </div>
        <div>
          <h2 className='text-lg font-semibold text-gray-800'>
            {t("Empty or deleted order")}
          </h2>
          <p className='text-sm text-gray-500 mt-1'>{t("No products")}</p>
        </div>
        <Link
          to='/dashboard/my_orders'
          className='inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors'
        >
          <FiArrowLeft size={14} /> {t("Back to orders")}
        </Link>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6 bg-gray-50 min-h-screen p-4 sm:p-2 md-lg:w-full w-[80%]'>
      {/* ── Page header ── */}
      <div className='flex flex-col sm:mr-l justify-between gap-3'>
        <div>
          <Link
            to='/dashboard/my_orders'
            className='inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-2 transition-colors'
          >
            <FiArrowLeft size={13} /> {t("Back to orders")}
          </Link>
          <h1 className='text-xl font-bold text-gray-900'>
            {t("Order")}{" "}
            <span className='text-blue-600'>#{MyOrder.orderId}</span>
          </h1>
          <p className='text-sm text-gray-500 mt-0.5'>{MyOrder.date}</p>
        </div>
        <div className='flex gap-2'>
          <StatusBadge value={MyOrder.payment_status} type='payment' />
          <StatusBadge value={MyOrder.delivery_status} type='delivery' />
          {/* Row 2 — buyer section */}
          <div className='flex items-center'>
            <BuyerSection order={MyOrder} />
          </div>
        </div>
      </div>

      {/* ── Summary card ── */}
      <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-5 grid grid-cols-2 sm:grid-cols-1 gap-6'>
        {/* Shipping */}
        <div className='flex flex-col gap-2'>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5'>
            <FiMapPin size={12} /> {t("Shipping address")}
          </p>
          <AddressDisplay address={MyOrder.shippingInfo} />
        </div>
        {/* Order summary */}
        <div className='flex flex-col gap-2'>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5'>
            <FiPackage size={12} /> {t("Order summary")}
          </p>
          <div className='flex flex-col gap-1 text-sm text-gray-700'>
            <p className='flex items-center gap-1.5'>
              <FiMail size={13} className='text-gray-400' />
              {customerInfo.email}
            </p>
            <p className='flex items-center gap-2 mt-1'>
              <span className='text-gray-500'>{t("Total")} :</span>
              <span className='text-lg font-bold text-gray-900'>
                {parseFloat(MyOrder.price).toFixed(2)} {devise}
              </span>
            </p>
            <p className='text-xs text-gray-400'>
              {t("include shipping fees")}
            </p>
          </div>
        </div>
      </div>

      {/* ── Sellers ── */}
      <div className='flex flex-col gap-5'>
        {MyOrder.sellers?.map((seller, index) => (
          <div
            key={index}
            className='bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden'
          >
            {/* Seller header */}
            <div className='flex flex-wrap justify-between items-start gap-4 p-5 border-b border-gray-100 bg-gray-50/50'>
              <div className='flex flex-col gap-0.5'>
                <h2 className='text-base font-bold text-gray-900'>
                  {seller.shopInfo?.shopName || "Shop"}
                </h2>
                <p className='text-xs text-gray-500 flex items-center gap-1.5'>
                  <FiMail size={11} /> {seller.email}
                </p>
                {seller.shopInfo?.telephone && (
                  <p className='text-xs text-gray-500 flex items-center gap-1.5'>
                    <FiPhone size={11} /> {seller?.shopInfo?.telephone}
                  </p>
                )}
                {seller.shopInfo?.address && (
                  <p className='text-xs text-gray-500 flex items-center gap-1.5'>
                    <FiMapPin size={11} />
                    {seller.shopInfo.address}, {seller.shopInfo.city},{" "}
                    {seller.shopInfo.country}
                  </p>
                )}
              </div>
              <div className='items-center gap-2 inline-flex'>
                <button
                  onClick={() => handleSendOrderMessage(seller)}
                  className='inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition-colors shadow-sm'
                >
                  <FiMessageSquare size={13} />
                  ENVOI DIRECT
                </button>
                <WhatsAppButton
                  buttonName={"ENVOI WHATSAPP"}
                  sellerPhone={seller?.shopInfo?.telephone}
                  message={buildOrderMessage(seller)}
                />
              </div>
            </div>

            {/* Products + Map */}
            <div className='flex md-lg:flex-col gap-6 p-5 items-start'>
              {/* Products list */}
              <div className='flex-1 md-lg:w-full flex flex-col gap-4'>
                {seller.products?.map((product, i) => {
                  const variant = product.selectedVariant;
                  const base = variant ? variant.variantPrice : product.price;
                  const discounted = (
                    base *
                    (1 - (product.discount || 0) / 100)
                  ).toFixed(2);
                  const variantImage =
                    (variant?.variantImage?.url || variant?.variantImage) ??
                    product.images?.[0];
                  const stock = variant ? variant.variantStock : product.stock;

                  return (
                    <div
                      key={i}
                      className={`flex w-full sm:flex-col gap-4 ${i < seller.products.length - 1 ? "pb-4 border-b border-gray-100" : ""}`}
                    >
                      {/* Image + Info */}
                      <div className='flex gap-3 flex-1'>
                        <Link
                          to={`/product/details/${product._id}`}
                          className='flex-shrink-0'
                        >
                          <img
                            className='w-[72px] h-[72px] object-contain rounded-xl border border-gray-100 bg-gray-50 p-1'
                            src={variantImage?.url || variantImage}
                            alt={product.name}
                          />
                        </Link>
                        <div className='flex flex-col gap-1 text-sm'>
                          <Link
                            to={`/product/details/${product._id}`}
                            className='font-semibold text-gray-800 hover:text-blue-600 transition-colors leading-snug'
                          >
                            <TruncateText text={product.name} maxLength={42} />
                          </Link>
                          <p className='text-gray-500'>
                            {t("Brand")}:{" "}
                            <span className='text-gray-700 font-medium'>
                              {product.brand}
                            </span>
                          </p>
                          <p className='text-gray-500'>
                            {t("Quantity")}:{" "}
                            <span className='text-gray-700 font-medium'>
                              {product.quantity}
                            </span>
                          </p>
                          <div className='flex gap-2 flex-wrap mt-0.5'>
                            {variant?.color && (
                              <span className='text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full font-medium'>
                                {t("Color")}: {variant.color}
                              </span>
                            )}
                            {variant?.size && (
                              <span className='text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full font-medium'>
                                {t("Size")}: {variant.size}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Price + Actions */}
                      <div className='flex flex-col items-end sm:flex-row sm:w-full sm:justify-between 2xs:flex-col gap-2 min-w-[120px]'>
                        <div className='text-right sm:text-left'>
                          <p className='text-base font-bold text-orange-500'>
                            {discounted} {devise}
                          </p>
                          {product.discount > 0 && (
                            <div className='flex items-center gap-1.5 justify-end sm:justify-start'>
                              <p className='text-xs text-gray-400 line-through'>
                                {parseFloat(base).toFixed(2)} {devise}
                              </p>
                              <span className='text-xs text-red-500 font-semibold'>
                                -{product.discount}%
                              </span>
                            </div>
                          )}
                        </div>
                        {MyOrder.payment_status === "unpaid" && (
                          <div className='flex gap-2 items-center'>
                            <QuantityInput
                              product={product}
                              stockLimit={stock}
                              fonction='order'
                              cardId={product.cardId}
                              sellerId={seller.sellerId}
                              orderId={MyOrder.orderId}
                              onSuccess={() => dispatch(get_order({ orderId }))} // ← fix auto-refresh
                            />
                            <button
                              onClick={() => handleDeleteOrder(product)}
                              className='w-8 h-8 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition-colors'
                              title={t("Delete")}
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Map */}
              <div className='w-[200px] md-lg:w-full h-[140px] md-lg:h-[180px] rounded-xl overflow-hidden border border-gray-100 flex-shrink-0 bg-gray-50'>
                {seller.shopInfo?.lat && seller.shopInfo?.lon ? (
                  <iframe
                    src={`https://maps.google.com/maps?q=${seller.shopInfo.lat},${seller.shopInfo.lon}&z=15&output=embed`}
                    title='map'
                    className='w-full h-full'
                  />
                ) : (
                  <div className='flex flex-col items-center justify-center h-full gap-2 text-gray-400'>
                    <FiMapPin size={22} />
                    <p className='text-xs'>{t("Location not available")}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Seller total footer */}
            <div className='flex justify-between items-center px-5 py-3 bg-gray-50 border-t border-gray-100'>
              <p className='text-xs text-gray-400 flex items-center gap-1.5'>
                <FiTruck size={12} /> {seller.shopInfo?.shopName}
              </p>
              <p className='text-sm font-bold text-gray-900'>
                {seller.sellerTotal.toFixed(2)} {devise}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderDetails;
